/**
 * "Gearworks" — original roller/vaulter showcase (difficulty 4/10).
 * Slope rides into a roller flip corridor, vaulter walls, then a soft/mega
 * pad chain. Authored in block coordinates → standard SSL LevelData.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0, hanging = false): void =>
  push({ type: 'spike', ...cell(bx, by), ...(hanging ? { flipY: true } : {}) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};
const column = (bx: number, fromY: number, toY: number): void => {
  for (let by = fromY; by <= toY; by++) block(bx, by);
};
const slope = (bx: number, by: number, dir: 'up' | 'down'): void =>
  push({ type: 'slope', ...cell(bx, by), props: { dir, grade: 1 } });
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const pad = (bx: number, kind: string): void =>
  push({ type: 'pad', ...cell(bx, 0), props: { kind } });

// --- Runner + slope ride (0–48) --------------------------------------------------
spike(22);
spike(30);
spike(31);
slope(38, 0, 'up');
block(39, 0);
block(40, 0);
spike(40, 1); // spike on the plateau
block(41, 0);
slope(42, 0, 'down');

// --- Roller flip corridor (48–90) -------------------------------------------------
portal(50, 'form:roller');
ceiling(52, 88, 5);
spike(58);
spike(59); // ground → flip to ceiling
spike(66, 4, true);
spike(67, 4, true); // ceiling → flip down
spike(74);
spike(75);
spike(82, 4, true);
spike(83, 4, true); // ends grounded

// --- Vaulter walls (90–126) --------------------------------------------------------
portal(92, 'form:vaulter');
column(100, 0, 1);
spike(105);
column(110, 0, 1);
spike(115);
column(120, 0, 1);

// --- Pad chain (126–150) ------------------------------------------------------------
portal(126, 'form:runner');
pad(130, 'soft');
spike(131);
spike(132);
pad(140, 'mega');
spike(141);
spike(142);
spike(143);

// --- Finale (150–162) ---------------------------------------------------------------
spike(152);
spike(153);

// Secret shards.
push({ type: 'collectible', ...cell(35, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(78, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(136, 2), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(162, 2) });

export const GEARWORKS: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Gearworks',
    author: 'Shape Shifters Team',
    description: 'Roll the corridor, vault the walls, ride the pads.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 4,
    tags: ['forms', 'official'],
  },
  song: { id: 'neon-circuit', bpm: 128, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#fbbf24',
    hazardColor: '#f43f5e',
    backgroundColor: '#141003',
  },
  objects,
};
